// MACHINERY V2 (Stroje) — the SR create/edit form's data + behaviour (M2.6).
//
// Spec: data package §7 (SR Create) + §8 (SR Edit). Reference impl:
// components/CreateServiceReportPage.tsx — this is that page's logic half,
// unchanged in every observable way; SrCreateV2.tsx / SrCreateFields.tsx own
// the pixels (300-line cap).
//
// ONE COMPONENT SERVES BOTH MODES, AND THE MODE IS REDUX, NOT THE URL.
// `isEdit = !!serviceReport` (§8). The report identity is
// `serviceReport.number` FROM STATE — `id !== number`, and the PUT keys off
// number: `PUT /v1/customer/service-reports/{number}`. There is no edit URL, so
// a redesign that invented one would break the contract (§8, last line).
//
// PREFILL IS EXACTLY THE 8 FIELDS (§8). Every other field on the read model —
// statusCode, cost, solvingDescription, closeDate, sourceType, the RFS fields,
// the audit fields — is carried on the VM and NEVER touched here; the solve flow
// and the server own them. Edit does NOT re-fetch (§8): it trusts the list item,
// which already carries the full VM including each file dataURI content.
//
// >>> THREE v1 BEHAVIOURS ARE PRESERVED ON PURPOSE. DO NOT "FIX" THEM. <<<
//  1. THE openDate PICKER QUIRK. v1 binds `value={moment(field.value)}`
//     (CreateServiceReportPage.tsx:609) and `moment(undefined)` is NOW — so the
//     DATE control shows TODAY while the RHF value is still undefined, and only
//     a real interaction writes a value. The TIME control is bound separately
//     with `openDate ? moment(openDate) : null` (:660) so it renders EMPTY until
//     then. Submit closes the gap with `data.openDate = data.openDate ||
//     moment().toDate()` (:372). Net effect: an untouched form posts "now", and
//     the date the user saw is the date they get. srDateTime.ts reproduces all
//     three halves against native inputs — see its header.
//  2. THE typeCode-2 NO-OP. On edit, `setFiles(serviceReport.files || [])`
//     (:262) loads ALL files regardless of typeCode, so SOLVING documents
//     (typeCode 2) render as removable chips — but removing one is a server
//     no-op: `ChangeServiceReportAsync` calls
//     `AdjustFilesAsync(serviceReport, model.Files, ServiceReportFileTypeCode
//     .ReportDocument)` (GoToStockServer-machinery ServiceReportsService.cs:153)
//     and `AdjustFilesAsync` (:429-436) reconciles with the predicate
//     `x => fileTypeCode == null || x.TypeCode == fileTypeCode`, i.e. typeCode 1
//     only. Both lines personally read in the server worktree on branch
//     module/machinery (ticket process rule (a)). New uploads are typeCode 1.
//  3. NAVIGATION ON SUBMIT IS UNCONDITIONAL (:371-382) — the page leaves for the
//     machine profile even when the thunk REJECTED, so a failed save is silent.
//     §9 D7 ("stay open until the write confirms") is scoped to the add-machine
//     dialog (M2.7) and explicitly does NOT apply here. Preserved, and reported
//     to the manager rather than changed.
//
// `createdViaMidas` is never set and never registered, so it is absent from the
// payload entirely (§7). Do not surface it, do not send it.
//
// ONE ADDITION, DISCLOSED: `submitting` latches for the duration of the awaits
// so a second click cannot fire a second POST. It gates nothing else — the
// navigate, its unconditionality and the payload are byte-for-byte v1.
//
// THE LATCH IS RELEASED IN A `finally` (M2.6 review C8). Without it the latch
// was one-way: the submit button is `disabled={submitting}` and `navigate` was
// the only exit, so ANY throw between the latch and the navigate left the form
// permanently un-submittable with the user's typed report trapped behind it,
// recoverable only by reloading (and losing it). The `catch` also SPEAKS: it
// raises the v2 error toast through `setError`, which MachineryToastHost renders
// on this page. Note what the catch does NOT cover — a REJECTED thunk. Neither
// `dispatch(...)` here uses `.unwrap()`, so a rejection resolves normally and
// still falls through to the unconditional navigate (preserved behaviour 3,
// above). Only a genuine throw reaches the catch, and v1 skips its navigate on a
// throw too — so this is v1's behaviour plus a released latch and a message.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import { useCallback, useEffect, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  changeServiceReport,
  createServiceReport,
  getServiceReports,
  selectServiceReport,
} from "../../customerMachineServiceReportsSlice";
// The v2 toast host reads `customerMachinery.error` (MachineryToastHost.tsx:49),
// NOT the service-reports slice — so a failure spoken from here must be raised
// on that slice to be seen.
import { setError } from "../../customerMachinerySlice";
import {
  ChangeServiceReport,
  ServiceReportFile,
  ServiceReportFileTypeCode,
} from "../../models/ServiceReportsModel";

/** THIS SURFACE CAP. The Dokumenty tab is 11 MB — ticket §5 #6 keeps them
 *  per-surface, deliberately NOT unified. v1: CreateServiceReportPage.tsx:154. */
export const MAX_FILE_SIZE_MB = 10;

export const useSrCreateForm = (machineId: string) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);

  const serviceReport = useAppSelector((s) => s.customerServiceReports.serviceReport);
  const serviceReportsQuery = useAppSelector((s) => s.customerServiceReports.serviceReportsQuery);

  const isEdit = !!serviceReport;
  const serviceReportNumber = serviceReport?.number || undefined;

  const [files, setFiles] = useState<ServiceReportFile[]>([]);
  const [fileError, setFileError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<ChangeServiceReport>();
  const { reset } = form;

  // ── Prefill / clear — EXACTLY the 8 fields of §8 ────────────────────────
  useEffect(() => {
    if (serviceReport) {
      reset({
        title: serviceReport.title,
        description: serviceReport.description,
        notes: serviceReport.notes || "",
        alarmCode: serviceReport.alarmCode || "",
        urgency: serviceReport.urgency,
        openDate: serviceReport.openDate,
        affectedPartCategoryId: serviceReport.affectedPartCategoryId,
        files: serviceReport.files,
      });
      // Preserved behaviour 2 — ALL files, typeCode included. See the header.
      setFiles(serviceReport.files || []);
    } else {
      reset({
        title: "",
        description: "",
        notes: "",
        alarmCode: "",
        urgency: undefined,
        openDate: undefined,
        affectedPartCategoryId: undefined,
        files: null,
      });
      setFiles([]);
    }
  }, [serviceReport, reset]);

  // The edit target is a SHARED slot; leaving it set would turn the next
  // "create" into an edit (v1 does the same on unmount, :278-283).
  useEffect(
    () => () => {
      dispatch(selectServiceReport(null));
    },
    [dispatch]
  );

  // ── Files: LOCAL state, never RHF; injected at submit (§7) ───────────────
  const readFile = useCallback(
    (file: File): Promise<ServiceReportFile | null> => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(translate("common:uploadFile.fileIsTooBig"));
        return Promise.resolve(null);
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () =>
          resolve({
            name: file.name,
            content: reader.result as string,
            size: file.size,
            typeCode: ServiceReportFileTypeCode.reportDocument,
          });
        reader.onerror = () => resolve(null);
      });
    },
    [translate]
  );

  const addFiles = useCallback(
    async (incoming: File[]) => {
      setFileError(undefined);
      const results = await Promise.all(incoming.map((f) => readFile(f)));
      const valid = results.filter((f): f is ServiceReportFile => f !== null);
      if (valid.length > 0) setFiles((prev) => [...prev, ...valid]);
    },
    [readFile]
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_unused, i) => i !== index));
  }, []);

  // ── Submit ───────────────────────────────────────────────────────────────
  // Read through a ref so the handler RHF captured at mount cannot post an
  // empty list after the user attached something (v1 closes over `files` and
  // is re-created every render; this hook hands out one stable submit).
  const filesRef = useRef(files);
  filesRef.current = files;

  const onSubmit: SubmitHandler<ChangeServiceReport> = async (data) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Preserved behaviour 1, final half: an untouched openDate becomes now.
      data.openDate = data.openDate || new Date();
      data.files = filesRef.current;
      if (isEdit && serviceReportNumber) {
        await dispatch(changeServiceReport({ model: data, serviceReportNumber }));
      } else if (machineId) {
        await dispatch(createServiceReport({ model: data, machineId }));
      }
      if (machineId) {
        await dispatch(getServiceReports({ machineId, query: serviceReportsQuery }));
      }
      // Preserved behaviour 3 — unconditional, rejection included. See the header.
      navigate(`/machinery-v2/${machineId}`);
    } catch {
      // Not the rejected-thunk path (see the header) — this is a genuine throw,
      // where v1 would also stay on the form, silently. Say so instead.
      dispatch(setError(translate("myMachinery:v2.sr.saveFailed")));
    } finally {
      // C8: the latch is a double-click guard, not a one-way door.
      setSubmitting(false);
    }
  };

  return {
    form,
    isEdit,
    files,
    fileError,
    addFiles,
    removeFile,
    submitting,
    submit: form.handleSubmit(onSubmit),
  };
};

export default useSrCreateForm;
